import { Ajax } from "./Ajax";
import { GlobalData } from "./GlobalData";

export class Notification {
    private notification: kendo.ui.Notification;
    private readonly customOnHideSuccess?: () => void;
    private successDisplayed: boolean = false;

    public constructor(customOnHideSuccess?: () => void) {
        this.customOnHideSuccess = customOnHideSuccess;

        let element = $('#notification');
        if (!element || element.length == 0) {
            element = $('<div id="notification"></div>').appendTo(document.body);
        }

        this.notification = element.kendoNotification({
            autoHideAfter: 0,
            button: true,
            show: (e: any) => {
                this.onShow(e);
            },
            hide: () => {
                this.onHide();
            },
        }).data('kendoNotification')!;
    }

    public success(message: string) {
        this.notification.success(message);
        this.successDisplayed = true;
    }

    public error(message: string) {
        this.notification.error(message);
    }

    private onShow(e: any) {
        if (e.sender.getNotifications().length == 1) {
            let element = e.element.closest(".k-animation-container"),
                eWidth = element.width(),
                eHeight = element.height(),
                wWidth = $(window).width() ?? 1000,
                wHeight = $(window).height() ?? 1000,
                newTop, newLeft;

            newLeft = Math.floor(wWidth / 2 - eWidth / 2);
            newTop = Math.floor(wHeight / 2 - eHeight / 2);

            e.element.parent().parent().css({ top: newTop, left: newLeft });
        }
    }

    private onHide() {
        if (this.successDisplayed && this.customOnHideSuccess) {
            this.customOnHideSuccess();
        }
    }

    public static reloadPage() {
        window.location.reload();
    }
}

export class Confirm {
    private confirm: kendo.ui.Confirm;

    public constructor(content: string, onDone: () => any, onFail: () => any) {
        // Create the div each time because Kendo completely removes it afterward.
        const element = $('<div id="confirm"></div>').appendTo(document.body);
        const options: any = {
            content: content,
        };
        this.confirm = element.kendoConfirm(options).data('kendoConfirm')!;
        this.confirm.result.done(onDone).fail(onFail);
    }

    public show() {
        this.confirm.open();
    }
}

export class TemplateHelpers {
    public static getSymbolLinkTemplate(link: string) {
        return '# if (symbol == "S&P500") { #' +
            '#=symbol#' +
            '# } else { #' +
            '<a href="' + link + '#=symbol#" target="_blank">#=symbol#</a>' +
            '# } #';
    }

    public static getFormattedDateTemplate(varName: string) {
        return '# if (!' + varName + ') return "" #' +
            '# var newDate = new Date(' + varName + '); #' +
            '# var month = newDate.getMonth() + 1; #' +
            '# var dateOfMonth = newDate.getDate(); #' +
            '# var year = newDate.getFullYear(); #' +
            '# var formattedDate = month + "/" + dateOfMonth + "/" + year; #' +
            '<span>#=formattedDate#</span>';
    }

    public static getTradingDayTemplate(varName: string) {
        return '# var days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; #' +
            '# var formattedDate = new Date(' + varName + '); #' +
            '# var tradingDay = days[formattedDate.getDay()] #' +
            '# if (tradingDay == "Fri") { #' +
            '<span style="color: navy; font-weight: bold; font-size: 12px" >#=tradingDay#</span>' +
            '# } else { #' +
            '<span>#=tradingDay#</span>' +
            '# } #';
    }

    public static getColorCurrencyTemplate(varName: string) {
        return TemplateHelpers.getColorNumberTemplate(varName, "c2");
    }

    public static getColorPercentTemplate(varName: string, numDecimalPlaces: number = 1) {
        return TemplateHelpers.getColorNumberTemplate(varName, "p" + numDecimalPlaces);
    }

    public static getColorNumberTemplate(varName: string, numberFormat: string) {
        return '# var formattedValue = kendo.toString(' + varName + ', "' + numberFormat + '") || "" #' +
            '# if (' + varName + ' < 0) { # ' +
            '<span class="grid-number-negative" >#=formattedValue#</span>' +
            '# } else if (' + varName + ' > 0) { # ' +
            '<span class="grid-number-positive" >#=formattedValue#</span>' +
            '# } else { # ' +
            '<span class="grid-number-zero" >#=formattedValue#</span>' +
            '# } #';
    }

    public static getBgColorCurrencyTemplate(varName: string) {
        return TemplateHelpers.getBgColorNumberTemplate(varName, "c2");
    }

    public static getBgColorPercentTemplate(varName: string, numDecimalPlaces: number) {
        return TemplateHelpers.getBgColorNumberTemplate(varName, "p" + numDecimalPlaces);
    }

    public static getBgColorNumberTemplate(varName: string, numberFormat: string) {

        return '# var formattedValue = kendo.toString(' + varName + ', "' + numberFormat + '") #' +
            '# if (' + varName + ' < 0) { #' +
            '<div class="grid-bg-color-number grid-bg-color-number-negative">#=formattedValue#</div>' +
            '# } else if (' + varName + ' > 0) { # ' +
            '<div class="grid-bg-color-number grid-bg-color-number-positive">#=formattedValue#</div>' +
            '# } else { # ' +
            '<div class="grid-bg-color-number grid-bg-color-number-zero">#=formattedValue#</div>' +
            '# } #';
    }

    public static getBoolTemplate(varName: string) {
        return '# if (' + varName + ') { #' +
            '<div><center>YES</center></div>' +
            '# } else { #' +
            '<div></div>' +
            '# } #';
    }

    public static getGreenBoolTemplate(varName: string, displayValueIfTrue: string = 'YES') {
        return this.getColorBoolTemplate(varName, 'green', displayValueIfTrue);
    }

    public static getYellowBoolTemplate(varName: string, displayValueIfTrue: string = 'YES') {
        return this.getColorBoolTemplate(varName, 'yellow', displayValueIfTrue);
    }

    public static getRedBoolTemplate(varName: string, displayValueIfTrue: string = 'YES') {
        return this.getColorBoolTemplate(varName, 'red', displayValueIfTrue);
    }

    private static getColorBoolTemplate(varName: string, color: string, displayValueIfTrue: string) {
        return `# if (${varName}) { #` +
            `<div class="grid-${color}-bool-true"><center>${displayValueIfTrue}</center></div>` +
            `# } else { #` +
            `<div></div>` +
            `# } #`;
    }

    public static getNormalizedTemplate(value: string | null = null,
        defaultValue: string | null = '') {

        return `# var value = ${value}; #` +
            `# var defaultValue = "${defaultValue}"; #` +
            `# if (value >= 80) { #` +
            `<div class="grid-grade grid-grade-A"><center>#=value #</center></div>` +
            `# } else if (value >= 60) { #` +
            `<div class="grid-grade grid-grade-B"><center>#=value #</center></div>` +
            `# } else if (value >= 40) { #` +
            `<div class="grid-grade grid-grade-C"><center>#=value #</center></div>` +
            `# } else if (value >= 20) { #` +
            `<div class="grid-grade grid-grade-D"><center>#=value #</center></div>` +
            `# } else if (value > 0 || value === 0) { #` +
            `<div class="grid-grade grid-grade-E"><center>#=value #</center></div>` +
            `# } else if (defaultValue !== '') { #` +
            `<div class="grid-grade grid-grade-Default"><center>#=defaultValue #</center></div>` +
            `# } else { #` +
            `<div></div>` +
            `# } #`;
    }

    public static getGradeTemplate(grade: string, value: string | null = null,
        defaultValue: string | null = '', circleValue: string | null = '',
        antsValue: string | null = '', triangleValue: string | null = '') {

        return `# var grade = ${grade}; #` +
            `# var value = ${value || grade}; #` +
            `# var defaultValue = "${defaultValue}"; #` +
            `# var circleValue = ${circleValue || 'null'}; #` +
            `# var antsValue = ${antsValue || 'null'}; #` +
            `# var triangleValue = ${triangleValue || 'null'}; #` +
            `# if (circleValue && circleValue !== 0) { value += "●"; } #` +
            `# if (antsValue && antsValue !== 0) { value += "🐜"; } #` +
            `# if (triangleValue && triangleValue !== 0) { value += "▲"; } #` +
            `# if (grade == 'A') { #` +
            `<div class="grid-grade grid-grade-A"><center>#=value #</center></div>` +
            `# } else if (grade == 'B') { #` +
            `<div class="grid-grade grid-grade-B"><center>#=value #</center></div>` +
            `# } else if (grade == 'C') { #` +
            `<div class="grid-grade grid-grade-C"><center>#=value #</center></div>` +
            `# } else if (grade == 'D') { #` +
            `<div class="grid-grade grid-grade-D"><center>#=value #</center></div>` +
            `# } else if (grade == 'E') { #` +
            `<div class="grid-grade grid-grade-E"><center>#=value #</center></div>` +
            `# } else if (defaultValue != '') { #` +
            `<div class="grid-grade grid-grade-Default"><center>#=defaultValue #</center></div>` +
            `# } else { #` +
            `<div></div>` +
            `# } #`;
    }

    public static getGreenGradeTemplate(grade: string, value: string | null = null,
        defaultValue: string | null = '') {

        return `# var grade = ${grade}; #` +
            `# var value = ${value || grade}; #` +
            `# var defaultValue = "${defaultValue}"; #` +
            `# if (grade == 'A') { #` +
            `<div class="grid-grade grid-grade-A"><center>#=value #</center></div>` +
            `# } else if (grade == 'B') { #` +
            `<div class="grid-grade grid-green-grade-B"><center>#=value #</center></div>` +
            `# } else if (grade == 'C') { #` +
            `<div class="grid-grade grid-green-grade-C"><center>#=value #</center></div>` +
            `# } else if (grade == 'D') { #` +
            `<div class="grid-grade grid-green-grade-D"><center>#=value #</center></div>` +
            `# } else if (grade == 'E') { #` +
            `<div class="grid-grade grid-green-grade-E"><center>#=value #</center></div>` +
            `# } else if (defaultValue != '') { #` +
            `<div class="grid-grade grid-grade-Default"><center>#=defaultValue #</center></div>` +
            `# } else { #` +
            `<div></div>` +
            `# } #`;
    }

    public static getCheckboxTemplate(varName: string) {
        return '# var className = "' + varName + 'Checkbox" #' +
            '# var checkBoxId = className + "_" + symbol; #' +
            '# if (' + varName + ') { #' +
            '<input type="checkbox" id="#= checkBoxId#" class="#= className# k-checkbox" checked="checked" />' +
            '# } else { #' +
            '<input type="checkbox" id="#= checkBoxId#" class="#= className# k-checkbox" />' +
            '# } #';
    }

    public static getDeleteButtonTemplate(path: string, id: string) {
        return '<img src="' + path +
            '/delete.png" class="delete-button" id="delete-button#= ' + id + '#" />';
    }

    public static getDeleteButtonClass() {
        return 'delete-button';
    }

    public static getPivotPointPatternTemplate(varName: string, flag: string) {
        return '# if (' + flag + ') { #' +
            '<span style="color: green">#=' + varName + '#</span>' +
            '# } else { #' +
            '<span>#=' + varName + '#</span>' +
            "# } #";
    }

    public static getValueLineReportTemplate(url: string) {
        return '# if ( ' + url + ') { #' +
            '<a href="#=' + url + '#" target="_blank">pdf</a>' +
            '# } #';
    }

    public static getHeatMapTemplate() {
        return '<div class="heat-map-item" style="background-color: #:color#">' +
            '<div style="font-weight: bold; font-size: 18px">#:symbol#</div>' +
            '<div>#:sumQtyOwn# shares</div>' +
            '<div>#= kendo.toString(totalGainLoss, "c")#</div>' +
            '<div>#= kendo.toString(totalGainLossPct / 100, "p1")#</div>' +
            '</div>';
    }

    public static getCandlestick(path: string, closePosition: string,
        closeUpFromYesterday: string) {

        return '# var quartile = Math.abs(' + closePosition + '); #' +
            '# var closeUpFromYesterday = ' + closeUpFromYesterday + '; #' +
            '# if (quartile >= 0.75) { #' +
                '# if (closeUpFromYesterday) { #' +
                    '<img src="' + path + '/Candlestick_Blue4.png" style="height: 24px; margin: 2px;" />' +
                '# } else { #' +
                    '<img src="' + path + '/Candlestick_Red4.png" style="height: 24px; margin: 2px;" />' +
                '# } #' +
            '# } else if (quartile >= 0.5) { #' +
                '# if (closeUpFromYesterday) { #' +
                    '<img src="' + path + '/Candlestick_Blue3.png" style="height: 24px; margin: 2px;" />' +
                '# } else { #' +
                    '<img src="' + path + '/Candlestick_Red3.png" style="height: 24px; margin: 2px;" />' +
                '# } #' +
            '# } else if (quartile >= 0.25) { #' +
                '# if (closeUpFromYesterday) { #' +
                    '<img src="' + path + '/Candlestick_Blue2.png" style="height: 24px; margin: 2px;" />' +
                '# } else { #' +
                    '<img src="' + path + '/Candlestick_Red2.png" style="height: 24px; margin: 2px;" />' +
                '# } #' +
            '# } else { #' +
                '# if (closeUpFromYesterday) { #' +
                    '<img src="' + path + '/Candlestick_Blue1.png" style="height: 24px; margin: 2px;" />' +
                '# } else { #' +
                    '<img src="' + path + '/Candlestick_Red1.png" style="height: 24px; margin: 2px;" />' +
                '# } #' +
            '# } #';
    }
}

export class ValueSwapHelper {
    public static showHideFields(gridId: string, showColumns: string[],
        hideColumns: string[]) {

        const grid = $(gridId).data('kendoGrid')!;
        for (let col of hideColumns) {
            grid.hideColumn(col);
        }
        for (let col of showColumns) {
            grid.showColumn(col);
        }
    }
}

export class ReportDateHelper {
    private static REPORT_DATE_ID = '#reportDate';

    public static showReportDate(reportDateStr: string | null) {
        if (!reportDateStr) {
            return null;
        }

        const reportDate = new Date(reportDateStr);
        const formattedDate = (reportDate.getMonth() + 1) + '/' +
            reportDate.getDate() + '/' + reportDate.getFullYear();
        const reportDateElement = $(ReportDateHelper.REPORT_DATE_ID);
        reportDateElement.html('<b>Report Date: </b>' + formattedDate);
        const width = reportDateElement.outerWidth()!;
        reportDateElement.parent().parent().width(width + 3);

        return reportDate;
    }
}

class BigPictureModel {
    public dailyAction!: string;
    public currentOutlook!: string;
    public distributionDays!: string;
    public pctInvest!: number;
}

export class BigPictureHelper {
    private static readonly DAILY_ACTION = '#dailyAction';
    private static readonly CURRENT_OUTLOOK = '#currentOutlook';
    private static readonly DISTRIBUTION_DAYS = '#distributionDays';
    private static readonly PCT_INVEST = '#pctInvest';

    public static showBigPictureData() {
        const urlSuffix = GlobalData.URL_SUFFIX_IBD_LEADERBOARD_THE_BIG_PICTURE +
            '?numDays=1';

        const success = (data: BigPictureModel[]) => {
            if (data && data.length == 1) {
                $(BigPictureHelper.DAILY_ACTION).html('<b>Daily Action</b><br>' +
                    data[0].dailyAction);
                $(BigPictureHelper.CURRENT_OUTLOOK).html('<b>Current Outlook</b><br>' +
                    data[0].currentOutlook);
                $(BigPictureHelper.DISTRIBUTION_DAYS).html('<b>Distribution Days</b><br>' +
                    data[0].distributionDays);
                $(BigPictureHelper.PCT_INVEST).html('<b>Percent Invest</b><br>' +
                    kendo.format('{0:p0}', data[0].pctInvest));
            }
        };

        Ajax.invoke(urlSuffix, 'GET', null, success);
    }
}

export class AutoCompleteHelper {
    public static getEditor(container: any, options: any) {
        return $('<input data-bind="value:' + options.field + '"/>')
            .appendTo(container);
    }

    public static selectText(editor: any, isPercent: boolean = false, isDate: boolean = false) {
        window.setTimeout(() => {
            if (!editor || !editor.length) {
                return;
            }

            if (isPercent) {
                const newValue = editor[0].value * 100;
                editor[0].value = newValue;
            } else if (isDate) {
                const parsedDate = kendo.parseDate(editor[0].value);
                if (parsedDate) {
                    editor[0].value = kendo.toString(parsedDate, 'M/d/yyyy');
                } else {
                    editor[0].value = '';
                }
            }

            editor.focus();
            editor.select();
        }, 50);
    }
}

export class GridHelper {
    public static getCurrentGridDataRow(gridData: kendo.data.ObservableArray, element: any): any {
        const tableRowUid = $(element.currentTarget.parentElement.parentElement).attr('data-uid');

        for (let gridRow = 0; gridRow < gridData.length; ++gridRow) {
            if (gridData[gridRow].uid == tableRowUid) {
                return gridData[gridRow];
            }
        }

        return null;
    }
}

export class ChartAxes {
    date: Date;
    value: number;

    constructor(date: string, value: number) {
        this.date = new Date(date);
        this.value = value;
    }
}

// Member vars need to be public to match Kend grid definition.
export class PortfolioGridFilter {
    public field: string;
    public operator: string;
    public value: string | number | boolean;

    public constructor(field: string, operator: string, value: string | number | boolean) {
        this.field = field;
        this.operator = operator;
        this.value = value;
    }
}

export class PortfolioGridGroup {
    private name: string;
    private detail: boolean;

    public constructor(name: string, detail: boolean) {
        this.name = name;
        this.detail = detail;
    }

    public getName() {
        return this.name;
    }

    public getDetail() {
        return this.detail;
    }
}

export class PortfolioGridField {
    private name: string;
    private filters: { [id: string]: PortfolioGridFilter; };
    private group: PortfolioGridGroup | null;

    public constructor(name: string, group: PortfolioGridGroup | null = null) {
        this.name = name;
        this.filters = {};
        this.group = group;
    }

    public getName() {
        return this.name;
    }

    public getFilters() {
        return this.filters;
    }

    public addFilter(filterName: string, filter: PortfolioGridFilter) {
        this.filters[filterName] = filter;
    }

    public getGroup() {
        return this.group;
    }
}

export class PortfolioFilterSet {
    private grid: kendo.ui.Grid;
    private fields: { [id: string]: PortfolioGridField; };
    private groupState: { [name: string]: boolean };
    private currFilterName: string;

    public constructor(grid: kendo.ui.Grid, fields: { [id: string]: PortfolioGridField; }) {
        this.grid = grid;
        this.fields = fields;
        this.groupState = {};
        this.currFilterName = '';
    }

    public getCurrFilterName() {
        return this.currFilterName;
    }

    public clear() {
        this.currFilterName = '';
        this.grid.dataSource.filter({});
    }

    public set(filterName: string) {
        const filters: PortfolioGridFilter[] = [];
        this.currFilterName = filterName;

        for (let fieldKey of Object.keys(this.fields)) {
            for (let filterKey of Object.keys(this.fields[fieldKey].getFilters())) {
                if (filterKey.startsWith(filterName)) {
                    filters.push(this.fields[fieldKey].getFilters()[filterKey]);
                }
            }
        }

        this.grid.dataSource.filter(filters);
    }

    public setGroupState(name: string, state: boolean) {
        this.groupState[name] = state;
    }

    public updateGroupState(name: string, state: boolean) {
        this.setGroupState(name, state);

        for (let i = 0; i < this.grid.columns.length; ++i) {
            for (let j = 0; j < this.grid.columns[i].columns?.length; ++j) {
                const column = this.grid.columns[i].columns[j];
                const columnGroupName = column.myField?.getGroup()?.getName();
                if (columnGroupName === name) {
                    if (this.columnAndGroupStateMatch(column)) {
                        this.grid.showColumn(column.field);
                    } else {
                        this.grid.hideColumn(column.field);
                    }
                }
            }
        }
    }

    public showAllColumns() {
        for (let i = 0; i < this.grid.columns.length; ++i) {
            if (this.grid.columns[i].filterable) {
                this.grid.showColumn(this.grid.columns[i].field);
            }
            for (let j = 0; j < this.grid.columns[i].columns?.length; ++j) {
                const column = this.grid.columns[i].columns[j];
                if (column.filterable) {
                    if (this.columnAndGroupStateMatch(column)) {
                        this.grid.showColumn(column.field);
                    } else {
                        this.grid.hideColumn(column.field);
                    }
                }
            }
        }
    }

    public hideAllColumns() {
        for (let i = 0; i < this.grid.columns.length; ++i) {
            if (this.grid.columns[i].filterable) {
                this.grid.hideColumn(this.grid.columns[i].field);
            }
            for (let j = 0; j < this.grid.columns[i].columns?.length; ++j) {
                if (this.grid.columns[i].columns[j].filterable) {
                    this.grid.hideColumn(this.grid.columns[i].columns[j].field);
                }
            }
        }
    }

    public showFilteredColumns() {
        for (let fieldKey of Object.keys(this.fields)) {
            for (let filterKey of Object.keys(this.fields[fieldKey].getFilters())) {
                if (filterKey.startsWith(this.currFilterName)) {
                    this.grid.showColumn(this.fields[fieldKey].getName());
                }
            }
        }
    }

    private columnAndGroupStateMatch(column: any) {
        const groupName = column.myField?.getGroup()?.getName();
        if (groupName) {
            const groupState = this.groupState[groupName];
            const columnDetail = column.myField.getGroup().getDetail();
            if (groupState !== undefined && columnDetail !== undefined &&
                groupState !== columnDetail) {

                return false;
            }
        }

        return true;
    }
}
